import { useCallback, useEffect, useRef, useState } from 'react';
import type { PicksFilterValue } from '@/utils/ballotPicks';

// Listed in `PERSISTENT_KEYS` in storageUtils, so it survives the `beforeunload`
// wipe on refresh but is cleared by `clearVoteMateStorageOnLogout` on sign-out.
const STORAGE_KEY = 'votemate_election_filters';

/** Keyed by election identity: one master switch per election. */
type ElectionFilterMap = Record<string, PicksFilterValue>;

const readFromStorage = (): ElectionFilterMap => {
  try {
    // The per-contest and per-level filters this replaced. `clearVoteMateStorage`
    // drops them on unload now that they are off `PERSISTENT_KEYS`, but
    // `beforeunload` is not guaranteed to run (a discarded mobile tab, for one),
    // so clear them here too rather than leaving dead keys in storage.
    localStorage.removeItem('votemate_contest_filters');
    localStorage.removeItem('votemate_level_filters');
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const result: ElectionFilterMap = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (value === 'picks' || value === 'all') {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
};

/**
 * Per-election filter state (`'picks' | 'all'`) — the master switch that narrows
 * a whole ballot to the voter's hearts — persisted across refresh under the
 * `votemate_election_filters` key, paired with `useLikedCandidates` so a user's
 * "Your Picks" view is restored automatically when they return.
 *
 * `toggleFilter` returns the filter now in effect, mirroring mobile's
 * `ElectionFiltersService.toggle`, so a caller's follow-up work (expanding the
 * ballot's sections, the analytics payload) acts on the authoritative value
 * rather than on what the last render happened to show.
 */
const useElectionFilters = (): {
  filters: ElectionFilterMap;
  toggleFilter: (electionKey: string) => PicksFilterValue;
} => {
  const [filters, setFilters] = useState<ElectionFilterMap>(readFromStorage);
  // `toggleFilter` is the only writer, and it updates this ref before the state
  // it schedules commits — so back-to-back toggles each see the previous one.
  const latest = useRef(filters);
  // Skip the no-op write on first render — the state was just hydrated from
  // localStorage, so persisting it back is pointless and writes `{}` every page
  // load for users who've never used the feature.
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      // Store only the elections set to picks: 'all' is the default a missing
      // key already means, and mobile's store writes the same shape, so a blob
      // written by one client reads identically on the other.
      const picksOnly = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value === 'picks')
      );
      localStorage.setItem(STORAGE_KEY, JSON.stringify(picksOnly));
    } catch {
      // localStorage may be full / disabled (private mode, quota) — fail silently.
    }
  }, [filters]);

  const toggleFilter = useCallback((electionKey: string): PicksFilterValue => {
    const next: PicksFilterValue =
      (latest.current[electionKey] ?? 'all') === 'picks' ? 'all' : 'picks';
    const updated = { ...latest.current };
    // 'all' is the default a missing key already means.
    if (next === 'picks') updated[electionKey] = next;
    else delete updated[electionKey];
    latest.current = updated;
    setFilters(updated);
    return next;
  }, []);

  return { filters, toggleFilter };
};

export default useElectionFilters;
